import { useMemo } from 'react';
import tw, { styled } from 'twin.macro';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon, ChatBubbleOvalLeftEllipsisIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';

import { useAnalytics } from '@restworld/utils-common';
import { Link, Title, Alert } from '@restworld/ui-ds';
import { Avatar, Tooltip } from '@restworld/ui-ds-base';

import { candidateModalVariantAtom, selectedJobApplicationAtom } from '../../../employer-job-listing.atoms';
import { useKanbanRowsAndMatchStatusKeys } from '../../../hooks';

interface CandidateModalHeaderProps {
  email?: string;
  image?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  cvLink?: string;
}

function CandidateModalHeader({ image, firstName, lastName, cvLink }: CandidateModalHeaderProps) {
  const { t } = useTranslation();
  const [variant, setVariant] = useAtom(candidateModalVariantAtom);
  const { trackEvent } = useAnalytics();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const kanbanRowsAndMatchStatusKeys = useKanbanRowsAndMatchStatusKeys();

  const currentStatus = useMemo(
    () => kanbanRowsAndMatchStatusKeys.find((row) => row.matchStatusKey.includes(selectedJobApplication.status_key)),
    [kanbanRowsAndMatchStatusKeys, selectedJobApplication.status_key]
  );

  if (variant === 'references') {
    return (
      <div tw="pt-3 lg:(pt-0)">
        <div tw="flex justify-between items-center mb-3 pb-1">
          <Title fontSize="base" fontWeight="medium" textColor="neutral-700">
            {t('common:references')}
          </Title>
          <Link onClick={() => setVariant('default')} StartIcon={ArrowLeftIcon} newTab={false}>
            {t('job-positions-page:back_to_candidate')}
          </Link>
        </div>
        <Alert
          wrapperStyling={tw`p-3 bg-green-50 mb-6 lg:mb-0`}
          text={t('restworld_verifies_references_by_directly_contacting_the_listed_venues_key')}
          textStyling={tw`text-base font-normal leading-6`}
          variant="success"
          icon={<InformationCircleIcon className="h-4 w-4 min-h-[16px] min-w-[16px] text-green-700" />}
        />
      </div>
    );
  }

  if (variant === 'notes' || variant === 'spontaneous_candidate_notes') {
    return (
      <div>
        <div tw="flex justify-between items-center mb-3">
          <Title fontSize="base" fontWeight="medium" textColor="neutral-700">
            {t('job-positions-page:comments')}
          </Title>
          <Link
            onClick={() => setVariant(variant === 'spontaneous_candidate_notes' ? 'spontaneous_candidate' : 'default')}
            StartIcon={ArrowLeftIcon}
            newTab={false}
          >
            {t('job-positions-page:back_to_candidate')}
          </Link>
        </div>
        <Alert
          wrapperStyling={tw`p-3 w-fit bg-green-50`}
          text={
            <span>
              {t('job-positions-page:your_comments_are')}{' '}
              <strong tw="font-bold">{t('job-positions-page:private')}</strong>{' '}
              {t('job-positions-page:and_they_will_be')}{' '}
              <strong>{t('job-positions-page:visible_to_only_your_team')}.</strong> <br />
              {t('job-positions-page:neither_your_restworld_manager_nor_the_candidate_will_be_able_to_read_them')}.
            </span>
          }
          textStyling={tw`text-base font-normal leading-6`}
          variant="success"
          icon={<InformationCircleIcon className="h-4 w-4 min-h-[16px] min-w-[16px] text-green-700" />}
        />
      </div>
    );
  }

  return (
    <>
      <Wrapper>
        <InfoWrapper>
          <Avatar src={image} firstName={firstName} lastName={lastName} size="lg" />
        </InfoWrapper>

        <CtaWrapper>
          <Link
            StartIcon={ChatBubbleOvalLeftEllipsisIcon}
            newTab={false}
            onClick={() => setVariant(variant === 'spontaneous_candidate' ? 'spontaneous_candidate_notes' : 'notes')}
          >
            {t('common:your_comments')}
          </Link>
          {!cvLink ? (
            <Tooltip content={t('common:cv_not_available_tooltip')} placement="bottom">
              <Link disabled StartIcon={ArrowDownTrayIcon} newTab={false}>
                {t('common:download_cv')}
              </Link>
            </Tooltip>
          ) : (
            <Link
              href={cvLink}
              StartIcon={ArrowDownTrayIcon}
              newTab={false}
              onClick={() => trackEvent('employer_download_CV', { stage: currentStatus?.title })}
            >
              {t('common:download_cv')}
            </Link>
          )}
        </CtaWrapper>
      </Wrapper>
    </>
  );
}

export default CandidateModalHeader;

const Wrapper = styled.div`
  ${tw`h-[88px] mb-4 flex justify-between items-center mt-0 lg:(mt-0 mb-8)`}
`;
const InfoWrapper = styled.div`
  ${tw`flex lg:(flex-row gap-4)`}
`;
const CtaWrapper = styled.div`
  ${tw`text-neutral-700 flex flex-col items-end`}
`;
